import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost/api/v1';

const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});



// Detect session_replaced (another device logged in)
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401 && error.response?.data?.reason === 'session_replaced') {
            window.dispatchEvent(new CustomEvent('nizus:session_replaced'));
        }
        return Promise.reject(error);
    }
);

// Pre-flight session check — call before starting exams/quizzes/courses
// Forces an immediate DB session check (bypasses the 60s cache in bootstrap)
api.checkSession = async () => {
    await api.get('/auth/keepalive.php?force_session_check');
    // If session was replaced, the interceptor above fires 'nizus:session_replaced'
    // and the promise rejects with 401, so callers can just await this.
};

// Real API call for Stats
api.getStats = async (levelId) => {
    try {
        const response = await api.get('/stats.php', {
            params: { level_id: levelId }
        });
        return response.data;
    } catch (error) {
        console.error('API Error (Stats):', error);
        throw error;
    }
};

api.getRewardsSummary = async () => {
    try {
        const response = await api.get('/rewards-summary.php');
        return response.data;
    } catch (error) {
        console.error('API Error (Rewards Summary):', error);
        throw error;
    }
};

api.getModules = async () => {
    try {
        const response = await api.get('/modules.php');
        return response.data;
    } catch (error) {
        console.error('API Error (Modules):', error);
        throw error;
    }
};

api.getExams = async (levelId) => {
    try {
        const params = { level_id: levelId };
        const response = await api.get('/exams.php', { params });
        return response.data;
    } catch (error) {
        console.error('API Error (Exams):', error);
        return [];
    }
};

api.getExamQuestionHistory = async (examId) => {
    try {
        const response = await api.get('/exams.php', {
            params: { action: 'question_history', exam_id: examId }
        });
        return response.data;
    } catch (error) {
        console.error('API Error (Exam Question History):', error);
        throw error;
    }
};

api.getAnnexeDownloadUrl = (examId) => {
    return `${api.defaults.baseURL}/exams.php?action=download_annexe&exam_id=${examId}`;
};

api.downloadAnnexeBlob = async (examId) => {
    const url = api.getAnnexeDownloadUrl(examId);
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
};

api.getExamCatalog = async (levelId = 'pstar') => {
    // Remove hardcoded devUserId=1028 default to avoid showing demo data to guests
    return api.getExams(levelId);
};

api.getHistory = async (devUserId, levelId) => {
    // devUserId is now ignored by backend, but we keep signature for now to avoid breaking calls
    // ideally should be refactored to remove it entirely
    try {
        const response = await api.get('/history.php', {
            params: { level_id: levelId }
        });
        return response.data;
    } catch (error) {
        console.error('API Error (History):', error);
        return [];
    }
};

api.getHistoryEntry = async (historyId, type) => {
    const params = { id: historyId };
    if (type) params.type = type;
    const response = await api.get('/history.php', { params });
    return response.data;
};

api.getSessionDetails = async (sessionId) => {
    try {
        const response = await api.get('/session_details.php', {
            params: { session_id: sessionId }
        });
        return response.data;
    } catch (error) {
        console.error('API Error (Session Details):', error);
        throw error;
    }
};

export const userService = {
    // For prototype: allow passing dev_user_id
    // For prototype: allow passing dev_user_id
    async getProfile() {
        try {
            const response = await api.get('/user.php');
            return response.data;
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Helper to get just orders, using the profile endpoint
    // Helper to get just orders, using the profile endpoint
    async getOrders() {
        try {
            // Default to 1028 for dev prototype if not provided
            // In real app, this would come from session/context
            const data = await this.getProfile();
            return data.orders || [];
        } catch (error) {
            console.error('API Error (Orders):', error);
            return [];
        }
    },

    async getSubscriptionPricing(levelVar) {
        try {
            const response = await api.get(`/access/subscription_pricing.php?levelVar=${levelVar}`);
            return response.data;
        } catch (error) {
            console.error('API Error (Subscription Pricing):', error);
            throw error;
        }
    },

    async createPaymentSession(data) {
        try {
            const response = await api.post('/payment/create_session.php', data);
            return response.data;
        } catch (error) {
            console.error('API Error (Payment):', error);
            throw error;
        }
    },

    async calculateTaxes(data) {
        try {
            const response = await api.post('/payment/calculate_taxes.php', data);
            return response.data;
        } catch (error) {
            console.error('API Error (Tax Calculation):', error);
            throw error;
        }
    },

    async getSessionStatus(sessionId) {
        try {
            const response = await api.get(`/payment/session_status.php?session_id=${sessionId}`);
            return response.data;
        } catch (error) {
            console.error('API Error (Session Status):', error);
            throw error;
        }
    },

    async login(email, password) {
        try {
            const response = await api.post('/auth/login.php', { email, password });
            return response.data;
        } catch (error) {
            console.error('Login Error:', error);
            throw error; // Let AuthContext handle the specific error (401 vs 500)
        }
    },

    async resendVerification(email, language = 'fr') {
        try {
            const response = await api.post('/auth/resend-verification.php', { email, language });
            return response.data;
        } catch (error) {
            console.error('Resend Verification Error:', error);
            throw error;
        }
    },

    async forgotPassword(email, language = 'fr') {
        try {
            const response = await api.post('/auth/forgot-password.php', { email, language });
            return response.data;
        } catch (error) {
            console.error('Forgot Password Error:', error);
            throw error;
        }
    },

    async resetPassword(token, password, language = 'fr') {
        try {
            const response = await api.post('/auth/reset-password.php', { token, password, language });
            return response.data;
        } catch (error) {
            console.error('Reset Password Error:', error);
            throw error;
        }
    },

    async register(email, password, firstName = '', lastName = '', language = 'fr', referralCode = '') {
        try {
            const response = await api.post('/auth/register.php', {
                email,
                password,
                firstName,
                lastName,
                language,
                referralCode
            });
            return response.data;
        } catch (error) {
            console.error('Registration Error:', error);
            throw error;
        }
    }
};

export const preferencesService = {
    async get() {
        try {
            const response = await api.get('/user/preferences.php');
            return response.data.dashboardPrefs || {};
        } catch (error) {
            console.error('API Error (Preferences GET):', error);
            return {};
        }
    },

    async save(partialPrefs) {
        try {
            const response = await api.post('/user/preferences.php', partialPrefs);
            return response.data.dashboardPrefs;
        } catch (error) {
            console.error('API Error (Preferences POST):', error);
            throw error;
        }
    }
};

export const examService = {
    async getQuestionsByExam(moduleId, category, examId = null, userId = null, { type = 'standard' } = {}) {
        try {
            const params = {
                module_id: moduleId,
                category: category,
                exam_id: examId,
                type
            };

            const response = await api.get('/questions.php', { params });
            return response.data;
        } catch (error) {
            console.error('API Error (Questions):', error);
            return [];
        }
    },

    async getExamAnswers(examId, userId = null, { type = 'standard' } = {}) {
        try {
            const params = {
                exam_id: examId,
                answers_only: 1,
                type
            };

            const response = await api.get('/questions.php', { params });
            return response.data;
        } catch (error) {
            console.error('API Error (Answers):', error);
            return {};
        }
    },

    async getAllActiveSessions() {
        try {
            const response = await api.get('/sessions.php');
            return response.data || {};
        } catch (error) {
            console.error('API Error (All Sessions):', error);
            return {};
        }
    },

    async getExamSession(sessionId) {
        if (!sessionId) return null;
        try {
            const response = await api.get('/sessions.php', { params: { exam_id: sessionId } });
            return response.data;
        } catch (error) {
            console.error('API Error (Get Session):', error);
            return null;
        }
    },

    async saveExamSession(sessionId, data) {
        if (!sessionId) return false;
        try {
            const response = await api.post('/sessions.php', {
                exam_id: sessionId,
                answers: data.answers || {},
                current_question: data.currentQuestionIndex || 0,
                timer: data.timeInSeconds || 0,
                mode: data.mode || 'classic',
                exam_type: data.examType || 'standard'
            });
            return response.data;
        } catch (error) {
            console.error('Error saving session:', error);
            return false;
        }
    },

    async saveExamHistory(sessionId, historyData) {
        if (!sessionId) return false;
        // Extract numeric ID if composite string (timestamp used as session ID)
        const id = sessionId.toString().includes('-') ? sessionId.split('-').pop() : sessionId;

        try {
            const response = await api.post('/history.php', {
                session_id: id,
                level_id: historyData.moduleId,
                category: historyData.category,
                score: historyData.score,
                duration: historyData.timeInSeconds,
                answers: historyData.answers,
                mode: historyData.mode,
                exam_type: historyData.examType || 'standard'
            });
            return response.data;
        } catch (error) {
            console.error('Error saving history:', error);
            return false;
        }
    },

    async markReviewed(historyId, type) {
        if (!historyId) return;
        const body = { action: 'mark_reviewed', history_id: historyId };
        if (type) body.type = type;
        await api.post('/history.php', body);
    },

    async clearExamSession(sessionId) {
        if (!sessionId) return false;
        const id = sessionId.toString().includes('-') ? sessionId.split('-').pop() : sessionId;

        try {
            await api.delete('/sessions.php', { params: { exam_id: id } });
            return true;
        } catch (error) {
            console.error('Error clearing session:', error);
            return false;
        }
    }
};

// Keyword Exams (virtual exams from keyword search)
api.searchKeywordQuestions = async (keywords, levelId, lang, operator = 'OR') => {
    try {
        const response = await api.post('/keyword-exams.php', {
            action: 'search',
            keywords,
            level_id: levelId,
            lang,
            operator
        });
        return response.data;
    } catch (error) {
        console.error('API Error (Keyword Search):', error);
        throw error;
    }
};

api.searchKeywordExams = async (keywords, levelId, lang, operator = 'OR') => {
    try {
        const response = await api.post('/keyword-exams.php', {
            action: 'search_exams',
            keywords,
            level_id: levelId,
            lang,
            operator
        });
        return response.data;
    } catch (error) {
        console.error('API Error (Keyword Exam Search):', error);
        throw error;
    }
};

api.getKeywordExamAnswers = async (questionIds) => {
    try {
        const response = await api.post('/keyword-exams.php', {
            action: 'answers',
            question_ids: questionIds
        });
        return response.data;
    } catch (error) {
        console.error('API Error (Keyword Answers):', error);
        return {};
    }
};

api.searchErrorQuestions = async (levelId, filterType = 'level', filterValue = null, errorMode = 'never') => {
    try {
        const response = await api.post('/keyword-exams.php', {
            action: 'errors_search',
            level_id: levelId,
            filter_type: filterType,
            filter_value: filterValue,
            error_mode: errorMode
        });
        return response.data;
    } catch (error) {
        console.error('API Error (Error Search):', error);
        throw error;
    }
};

api.searchRevisionQuestions = async (levelId, daysThreshold, filterType = 'level', filterValue = null) => {
    try {
        const response = await api.post('/keyword-exams.php', {
            action: 'revision_search',
            level_id: levelId,
            days_threshold: daysThreshold,
            filter_type: filterType,
            filter_value: filterValue
        });
        return response.data;
    } catch (error) {
        console.error('API Error (Revision Search):', error);
        throw error;
    }
};

api.suggestVuExamenQuestion = async (levelId, questionText, answer1, answer2, answer3, answer4) => {
    try {
        const response = await api.post('/exams.php?action=suggest_vu_question', {
            level_id: levelId,
            questionText,
            answer1,
            answer2: answer2 || '',
            answer3: answer3 || '',
            answer4: answer4 || '',
        });
        return response.data;
    } catch (error) {
        console.error('API Error (Suggest Vu Question):', error);
        throw error;
    }
};

api.getQuestionsByIds = async (questionIds) => {
    try {
        const response = await api.post('/keyword-exams.php', {
            action: 'questions',
            question_ids: questionIds
        });
        return response.data;
    } catch (error) {
        console.error('API Error (Questions by IDs):', error);
        return [];
    }
};

// Custom Exam History
api.saveCustomExamHistory = async (data) => {
    try {
        const response = await api.post('/keyword-exams.php', {
            action: 'save_history',
            level_id: data.levelId,
            exam_type: data.examType,
            config: data.config,
            question_ids: data.questionIds,
            date_start: data.dateStart,
            score: data.score,
            duration: data.duration,
            answers: data.answers,
            mode: data.mode,
            page_index: data.pageIndex ?? 0
        });
        return response.data;
    } catch (error) {
        console.error('API Error (Save Custom History):', error);
        throw error;
    }
};

api.getCustomExamHistory = async (levelId, examType = null) => {
    try {
        const response = await api.post('/keyword-exams.php', {
            action: 'list_history',
            level_id: levelId,
            exam_type: examType
        });
        return response.data;
    } catch (error) {
        console.error('API Error (Custom History List):', error);
        return [];
    }
};

api.getCustomExamHistoryEntry = async (historyId) => {
    try {
        const response = await api.post('/keyword-exams.php', {
            action: 'get_history_entry',
            history_id: historyId
        });
        return response.data;
    } catch (error) {
        console.error('API Error (Custom History Entry):', error);
        throw error;
    }
};

api.markCustomExamReviewed = async (historyId) => {
    try {
        await api.post('/keyword-exams.php', {
            action: 'mark_custom_reviewed',
            history_id: historyId
        });
    } catch (error) {
        console.error('API Error (Mark Custom Reviewed):', error);
    }
};

// Custom Exam Presets
api.getCustomExamPresets = async (levelId, presetType = null) => {
    try {
        const response = await api.post('/keyword-exams.php', {
            action: 'list_presets',
            level_id: levelId,
            preset_type: presetType
        });
        return response.data;
    } catch (error) {
        console.error('API Error (List Presets):', error);
        return [];
    }
};

api.saveCustomExamPreset = async (data) => {
    try {
        const response = await api.post('/keyword-exams.php', {
            action: 'save_preset',
            level_id: data.levelId,
            preset_type: data.presetType,
            name: data.name,
            config: data.config,
            result_count: data.resultCount ?? 0
        });
        return response.data;
    } catch (error) {
        console.error('API Error (Save Preset):', error);
        throw error;
    }
};

api.deleteCustomExamPreset = async (presetId) => {
    try {
        await api.post('/keyword-exams.php', {
            action: 'delete_preset',
            preset_id: presetId
        });
    } catch (error) {
        console.error('API Error (Delete Preset):', error);
        throw error;
    }
};

// === SCHOOL STATS ===
export const schoolService = {
    getStudents: () => api.get('/school-stats.php?action=students').then(r => r.data),
    getStudentStats: (studentId) => api.get(`/school-stats.php?action=student_stats&student_id=${studentId}`).then(r => r.data),
};

// === PROMO CODES ===
export const promoService = {
    validate: (code) => api.post('/promo.php?action=validate', { code }).then(r => r.data),
    getMyCodes: () => api.get('/promo.php?action=my_codes').then(r => r.data),
    getMyReferral: () => api.get('/promo.php?action=my_referral').then(r => r.data),
    invite: (email, language) => api.post('/promo.php?action=invite', { email, language }).then(r => r.data),
    resend: (email, language) => api.post('/promo.php?action=resend', { email, language }).then(r => r.data),
};

export default api;
